import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, X, Layers } from "lucide-react";
import NavBar from "@/components/NavBar";
import { api, formatDate } from "@/lib/api";

export default function BlogIndexPage() {
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || "";
  const category = params.get("category") || "";
  const queryString = tag ? `?tag=${encodeURIComponent(tag)}` : category ? `?category=${encodeURIComponent(category)}` : "";
  const filterLabel = tag ? `#${tag}` : category || "";
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["publicBlog", tag, category],
    queryFn: async () => (await api.get(`/blog${queryString}`)).data,
  });
  const clearFilter = () => setParams({});

  // Collect distinct categories across ALL posts (unfiltered) for the top navigation.
  const { data: allPosts = [] } = useQuery({
    queryKey: ["publicBlogAll"],
    queryFn: async () => (await api.get("/blog")).data,
    enabled: !!filterLabel,  // only fetch when we're filtered (so we don't double-fetch on the default view)
  });
  const knownCategories = Array.from(
    new Set(([...allPosts, ...posts]).flatMap((p) => p.categories || []))
  ).sort();

  return (
    <>
      <NavBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">Notes from the homestead</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mt-2">The Graceful Garden Journal</h1>
          <p className="text-gg-muted mt-3 max-w-2xl">
            Tips, lessons, and seasonal notes from a working Arizona homestead — written for the families and students we coach.
          </p>
          {/* Filter chip row */}
          {filterLabel && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-gg-green/10 text-gg-green px-3 py-1.5 text-sm" data-testid="blog-filter-chip">
              Filtered by <strong>{filterLabel}</strong>
              <button type="button" onClick={clearFilter} className="hover:text-gg-brown" aria-label="Clear filter">
                <X size={14} />
              </button>
            </div>
          )}
          {/* Category quick-filters — shown when >=2 known cats */}
          {knownCategories.length >= 2 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="blog-category-nav">
              {knownCategories.map((c) => (
                <Link
                  key={c}
                  to={`/blog?category=${encodeURIComponent(c)}`}
                  className={`text-xs uppercase tracking-[0.14em] px-3 py-1 rounded-full border transition-colors ${
                    c === category
                      ? "bg-gg-green text-white border-gg-green"
                      : "bg-gg-surface border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green"
                  }`}
                >
                  {c}
                </Link>
              ))}
            </div>
          )}
        </header>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-64 rounded-2xl bg-gg-surface border border-gg-border animate-pulse" />)}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-gg-border bg-gg-surface p-16 text-center">
            <BookOpen className="mx-auto text-gg-brown" size={32} />
            <p className="font-heading text-2xl text-[var(--gg-text)] mt-4">
              {filterLabel ? `No posts match ${filterLabel}` : "First posts coming soon"}
            </p>
            <p className="text-gg-muted mt-2">
              {filterLabel
                ? "Try clearing the filter or browsing another category."
                : "We're cultivating the first batch of journal entries. Check back shortly!"}
            </p>
            {filterLabel ? (
              <button type="button" onClick={clearFilter} className="btn-pill btn-outline inline-flex mt-6">Clear filter</button>
            ) : (
              <Link to="/" className="btn-pill btn-outline inline-flex mt-6">Back home</Link>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="card-lift group rounded-2xl border border-gg-border bg-gg-surface overflow-hidden flex flex-col"
                data-testid={`blog-card-${p.slug}`}
              >
                {p.cover_image_url && (
                  <div className="aspect-[3/2] bg-gg-bg overflow-hidden">
                    <img src={p.cover_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
                    <time>{formatDate(p.published_at)}</time>
                    {p.is_hub && (
                      <span className="inline-flex items-center gap-1 text-gg-green normal-case tracking-normal">
                        · <Layers size={12} /> Hub
                      </span>
                    )}
                    {p.reading_time_minutes ? (
                      <span className="text-gg-muted normal-case tracking-normal">· {p.reading_time_minutes} min</span>
                    ) : null}
                  </div>
                  <h2 className="font-heading text-2xl text-[var(--gg-text)] mt-2 leading-tight group-hover:text-gg-green transition-colors">
                    {p.title}
                  </h2>
                  {p.excerpt && <p className="text-sm text-gg-muted mt-3 flex-1">{p.excerpt}</p>}
                  {(p.categories?.length > 0 || p.tags?.length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {(p.categories || []).slice(0, 2).map((c) => (
                        <span key={`c-${c}`} className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-gg-green/15 text-gg-green">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gg-green font-semibold mt-4 inline-flex items-center gap-1">
                    Read more <ArrowRight size={12} />
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
